import {
  MAX_OPTIONS_PER_QUESTION,
  MAX_QUESTIONS_PER_SCREEN,
  MAX_SCREEN_TIME_LIMIT_SECONDS,
  MIN_OPTIONS_PER_QUESTION,
  MIN_SCREEN_TIME_LIMIT_SECONDS,
  SCALE_PRESETS,
  type ScalePreset,
  scalePoints,
  type Screen,
} from '@floorpoll/shared';
import { useTourAnchor } from '../../lib/tour/anchors.js';
import { FollowUpEditor } from './FollowUpEditor.js';
import { ImageField } from './ImageField.js';
import { screenCardId } from './RunningOrder.js';

export const newId = (prefix: string) =>
  `${prefix}_${globalThis.crypto.randomUUID?.() ?? Math.random().toString(36).slice(2)}`;

export const emptyQuestion = () => ({
  questionId: newId('qn'),
  text: '',
  options: Array.from({ length: MIN_OPTIONS_PER_QUESTION }, () => ({
    optionId: newId('op'),
    text: '',
  })),
});

export const emptyScreen = (): Screen => ({ screenId: newId('sc'), questions: [emptyQuestion()] });

type Question = Screen['questions'][number];

/** What the answer-type picker is set to: multiple choice, or one of the scales. */
type AnswerType = 'choice' | ScalePreset;

const answerTypeOf = (question: Question): AnswerType =>
  question.type === 'scale' && question.scale ? question.scale : 'choice';

/**
 * Switches a question between multiple choice and a rating (#50).
 *
 * Rebuilds the options either way rather than trying to carry them across,
 * because the two kinds disagree about who owns them: a scale's points are
 * generated from its preset and `validateScreens` rejects anything else, while
 * a choice question's options are the author's to write. Carrying an NPS scale
 * over as text would also hand back eleven options, one past the cap a choice
 * question is held to.
 *
 * The whole question is replaced rather than patched, because moving to choice
 * has to *remove* `type`, `scale` and `multiSelect` — absent is what the
 * default is spelled as everywhere, and a stored `type: 'choice'` would be a
 * second spelling of it.
 */
function withAnswerType(question: Question, answerType: AnswerType): Question {
  const { type: _type, scale: _scale, multiSelect: _multiSelect, ...rest } = question;

  if (answerType === 'choice') return { ...rest, options: emptyQuestion().options };

  return {
    ...rest,
    type: 'scale',
    scale: answerType,
    options: scalePoints(answerType).map((point) => ({
      optionId: newId('op'),
      text: String(point),
    })),
  };
}

/** What turning the clock on starts at — long enough to read a question and answer it. */
export const DEFAULT_SCREEN_TIME_LIMIT_SECONDS = 30;

/**
 * Drops the limit rather than setting it to zero or null.
 *
 * `timeLimitSeconds` is optional and absent means "no clock" everywhere — the
 * validator, `deadlineFor`, and every reader. Writing a falsy value instead
 * would put a second spelling of the same state into the structure item.
 */
function withoutTimeLimit(screen: Screen): Screen {
  const { timeLimitSeconds: _cleared, ...rest } = screen;
  return rest;
}

interface Props {
  screen: Screen;
  /** #75 — an image upload is presigned per poll, so the control needs the id. */
  quizId: string;
  index: number;
  total: number;
  disabled: boolean;
  onChange: (screen: Screen) => void;
  onMove: (direction: -1 | 1) => void;
  onRemove: () => void;
  /**
   * The rule lives in questionMoves.ts, which this component deliberately does
   * not import: a step may cross into a neighbouring screen, and only QuizEditor
   * holds the whole poll.
   */
  canMoveQuestion: (questionIndex: number, direction: -1 | 1) => boolean;
  onMoveQuestion: (questionIndex: number, direction: -1 | 1) => void;
}

/**
 * Stories #15 and #16 — one screen's worth of authoring. A screen holds at
 * most three questions so the big screen stays readable from the back of a room.
 *
 * The card is the only box on this page: questions inside it are separated by a
 * rule rather than nested in a second bordered surface, and options are rows on
 * that same surface. Four levels of border-and-fill is what made the builder
 * read as assembled from parts, and the nesting carried no information the
 * labels were not already carrying.
 *
 * Controls sort into three ranks, and the rank decides the shape. Adding is a
 * full-width ghost row that trails the list it extends, so it reads as an
 * insertion point. Reordering and removing are icon controls that sit in the
 * head of whatever they act on, so a question's arrows are visibly the
 * question's. Nothing here is a filled button — the only one on the page is
 * Save.
 */
export function ScreenEditor({
  screen,
  quizId,
  index,
  total,
  disabled,
  onChange,
  onMove,
  onRemove,
  canMoveQuestion,
  onMoveQuestion,
}: Props) {
  // Only the first screen is an anchor. The guide's point is what a screen *is*,
  // which the first one makes as well as the fifth, and registering every screen
  // under one id would leave the spotlight on whichever mounted last.
  const screenAnchor = useTourAnchor('poll-screen');

  const updateQuestion = (questionIndex: number, patch: Partial<Question>) => {
    onChange({
      ...screen,
      questions: screen.questions.map((question, i) =>
        i === questionIndex ? { ...question, ...patch } : question,
      ),
    });
  };

  /** For a change that has to drop a field, which a patch cannot do. */
  const replaceQuestion = (questionIndex: number, next: Question) => {
    onChange({
      ...screen,
      questions: screen.questions.map((question, i) => (i === questionIndex ? next : question)),
    });
  };

  // A step is only ever refused at a screen's edge, for one of two reasons:
  // there is no neighbouring screen — which the disabled arrow already conveys,
  // exactly as it does for screens — or that neighbour is full, which is not
  // visible from here and so is worth saying. A third reason added to
  // canMoveQuestion would have to be reflected here, or this prints a
  // confidently wrong sentence.
  const refusalReason = (questionIndex: number, direction: -1 | 1) => {
    if (disabled || canMoveQuestion(questionIndex, direction)) return undefined;
    const neighbour = index + direction;
    if (neighbour < 0 || neighbour >= total) return undefined;
    return `Screen ${neighbour + 1} already holds ${MAX_QUESTIONS_PER_SCREEN} questions`;
  };

  return (
    <section
      className="screen-card"
      // Focus target for the running order's jump. Programmatic only: a screen
      // is a landmark to arrive at, not a stop on the tab route through the
      // fields inside it.
      id={screenCardId(screen.screenId)}
      tabIndex={-1}
      aria-labelledby={`screen-heading-${screen.screenId}`}
      ref={index === 0 ? screenAnchor : undefined}
    >
      <header className="screen-card-head">
        <span className="cue-number" aria-hidden="true">
          {String(index + 1).padStart(2, '0')}
        </span>
        <div className="screen-card-title">
          <h2 id={`screen-heading-${screen.screenId}`}>Screen {index + 1}</h2>
          <p className="screen-card-note">One page of the big screen</p>
        </div>
        <div className="icon-actions">
          <button
            type="button"
            className="icon-button"
            disabled={disabled || index === 0}
            onClick={() => onMove(-1)}
            title="Move this screen earlier"
            aria-label={`Move screen ${index + 1} earlier`}
          >
            ↑
          </button>
          <button
            type="button"
            className="icon-button"
            disabled={disabled || index === total - 1}
            onClick={() => onMove(1)}
            title="Move this screen later"
            aria-label={`Move screen ${index + 1} later`}
          >
            ↓
          </button>
          <span className="icon-actions-split" aria-hidden="true" />
          <button
            type="button"
            className="icon-button icon-destroy"
            disabled={disabled}
            onClick={onRemove}
            title="Delete this screen"
            aria-label="Delete screen"
          >
            ✕
          </button>
        </div>
      </header>

      {/* Story #77 — the screen's clock. Above the questions because it governs
          all of them: the unit is the screen, not the question, so an author
          needs to know the rule before the list it applies to. */}
      <div className="screen-timer-row">
        <label className="check-row" htmlFor={`timed-${screen.screenId}`}>
          <input
            id={`timed-${screen.screenId}`}
            type="checkbox"
            disabled={disabled}
            checked={screen.timeLimitSeconds !== undefined}
            onChange={(e) =>
              // Clearing removes the field rather than storing a zero: absent is
              // what "no clock" means everywhere else, and a zero would have to
              // be special-cased in the validator and on every reader.
              onChange(
                e.target.checked
                  ? { ...screen, timeLimitSeconds: DEFAULT_SCREEN_TIME_LIMIT_SECONDS }
                  : withoutTimeLimit(screen),
              )
            }
          />
          <span>Give the room a time limit on this screen</span>
        </label>

        {screen.timeLimitSeconds !== undefined ? (
          <span className="screen-timer-field">
            <input
              id={`time-limit-${screen.screenId}`}
              className="screen-timer-input"
              type="number"
              inputMode="numeric"
              min={MIN_SCREEN_TIME_LIMIT_SECONDS}
              max={MAX_SCREEN_TIME_LIMIT_SECONDS}
              step={1}
              disabled={disabled}
              aria-label={`Time limit for screen ${index + 1}, in seconds`}
              value={screen.timeLimitSeconds}
              onChange={(e) =>
                // Kept as typed, including a value outside the bounds:
                // validateScreens reports it in the same list as every other
                // authoring problem, and clamping here would silently rewrite
                // what the author entered mid-keystroke.
                onChange({ ...screen, timeLimitSeconds: e.target.valueAsNumber })
              }
            />
            <label className="screen-timer-unit" htmlFor={`time-limit-${screen.screenId}`}>
              seconds
            </label>
          </span>
        ) : null}
      </div>

      {screen.questions.length === 0 ? (
        <p className="screen-card-empty muted">
          This screen is empty. That is fine while drafting, but every screen needs a question
          before you can present.
        </p>
      ) : null}

      {screen.questions.map((question, questionIndex) => {
        const answerType = answerTypeOf(question);
        const shape = answerType === 'choice' ? null : SCALE_PRESETS[answerType];

        return (
          <div key={question.questionId} className="question-block">
            <div className="question-head">
              <label className="question-tag" htmlFor={`q-${question.questionId}`}>
                Question {questionIndex + 1}
              </label>
              <div className="icon-actions">
                {/* Ids, not refs: QuizEditor puts focus back on this arrow after a
                  question crosses to another screen, where React unmounts and
                  remounts it rather than moving the node. */}
                <button
                  type="button"
                  id={`move-${question.questionId}-earlier`}
                  className="icon-button"
                  disabled={disabled || !canMoveQuestion(questionIndex, -1)}
                  title={refusalReason(questionIndex, -1)}
                  aria-label={`Move question ${questionIndex + 1} on screen ${index + 1} earlier`}
                  onClick={() => onMoveQuestion(questionIndex, -1)}
                >
                  ↑
                </button>
                <button
                  type="button"
                  id={`move-${question.questionId}-later`}
                  className="icon-button"
                  disabled={disabled || !canMoveQuestion(questionIndex, 1)}
                  title={refusalReason(questionIndex, 1)}
                  aria-label={`Move question ${questionIndex + 1} on screen ${index + 1} later`}
                  onClick={() => onMoveQuestion(questionIndex, 1)}
                >
                  ↓
                </button>
                <span className="icon-actions-split" aria-hidden="true" />
                <button
                  type="button"
                  className="icon-button icon-destroy"
                  disabled={disabled}
                  title="Delete this question"
                  aria-label={`Delete question ${questionIndex + 1}`}
                  onClick={() =>
                    onChange({
                      ...screen,
                      questions: screen.questions.filter((_, i) => i !== questionIndex),
                    })
                  }
                >
                  ✕
                </button>
              </div>
            </div>

            <input
              id={`q-${question.questionId}`}
              className="question-input"
              value={question.text}
              disabled={disabled}
              placeholder="What do you want to ask?"
              onChange={(e) => updateQuestion(questionIndex, { text: e.target.value })}
            />

            {/* Story #75 — under the question it illustrates and above the
              answer type, because it belongs to what is being asked rather
              than to how it is answered. Offered on every kind: a rating's
              options are generated, but the thing being rated is still
              something an author may want to show. */}
            <ImageField
              quizId={quizId}
              kind="question"
              label={`Image for question ${questionIndex + 1}`}
              imageUrl={question.imageUrl}
              disabled={disabled}
              onChange={(imageUrl) => updateQuestion(questionIndex, { imageUrl })}
            />

            {/* Story #50 — above everything the answer governs, and first,
              because it decides whether the options below are the author's to
              write at all. One control rather than a kind and a scale: the
              kinds a scale comes in are the kinds on offer. */}
            <div className="answer-type-row">
              <label htmlFor={`answer-type-${question.questionId}`}>Answers</label>
              <select
                id={`answer-type-${question.questionId}`}
                className="answer-type-select"
                disabled={disabled}
                value={answerType}
                onChange={(e) =>
                  replaceQuestion(
                    questionIndex,
                    withAnswerType(question, e.target.value as AnswerType),
                  )
                }
              >
                <option value="choice">Multiple choice</option>
                {(Object.keys(SCALE_PRESETS) as ScalePreset[]).map((preset) => (
                  <option key={preset} value={preset}>
                    {SCALE_PRESETS[preset].label}
                  </option>
                ))}
              </select>
            </div>

            {shape ? (
              // The points are generated, so this is a preview and not an editor:
              // validateScreens rejects a hand-edited scale, and offering fields
              // that cannot be saved would be the builder disagreeing with the
              // gate behind it.
              <div className="scale-preview">
                <ol className="scale-points" aria-label={`${shape.label} points`}>
                  {scalePoints(answerType as ScalePreset).map((point) => (
                    <li key={point}>{point}</li>
                  ))}
                </ol>
                <p className="scale-anchors muted">
                  <span>{shape.minLabel}</span>
                  <span>{shape.maxLabel}</span>
                </p>
                <p className="muted scale-note">
                  The room taps one point. Switching the answer type replaces these.
                </p>
              </div>
            ) : (
              <>
                {/* Above the options, because it changes what tapping one of them
              does — an author needs the rule before the list it governs. */}
                <label className="check-row" htmlFor={`multi-${question.questionId}`}>
                  <input
                    id={`multi-${question.questionId}`}
                    type="checkbox"
                    disabled={disabled}
                    checked={question.multiSelect === true}
                    onChange={(e) =>
                      updateQuestion(questionIndex, { multiSelect: e.target.checked })
                    }
                  />
                  <span>Let people pick more than one answer</span>
                </label>

                <ol className="option-list">
                  {question.options.map((option, optionIndex) => (
                    <li key={option.optionId} className="option-block">
                      <div className="option-row">
                        <span className="option-index" aria-hidden="true">
                          {optionIndex + 1}
                        </span>
                        <input
                          aria-label={`Option ${optionIndex + 1} for question ${questionIndex + 1}`}
                          value={option.text}
                          disabled={disabled}
                          placeholder={`Option ${optionIndex + 1}`}
                          onChange={(e) =>
                            updateQuestion(questionIndex, {
                              options: question.options.map((o, i) =>
                                i === optionIndex ? { ...o, text: e.target.value } : o,
                              ),
                            })
                          }
                        />
                        <button
                          type="button"
                          className="icon-button icon-destroy"
                          disabled={disabled || question.options.length <= MIN_OPTIONS_PER_QUESTION}
                          title={
                            question.options.length <= MIN_OPTIONS_PER_QUESTION
                              ? `A question needs at least ${MIN_OPTIONS_PER_QUESTION} options`
                              : 'Remove this option'
                          }
                          onClick={() =>
                            updateQuestion(questionIndex, {
                              options: question.options.filter((_, i) => i !== optionIndex),
                            })
                          }
                          aria-label={`Remove option ${optionIndex + 1}`}
                        >
                          ✕
                        </button>
                      </div>
                      {/* #75 — only on a choice question. A rating's points are
                        generated from its preset and validateScreens rejects a
                        hand-edited list, so this whole branch is the editor and
                        the scale's is a preview. */}
                      <ImageField
                        quizId={quizId}
                        kind="option"
                        label={`Image for option ${optionIndex + 1}`}
                        imageUrl={option.imageUrl}
                        disabled={disabled}
                        onChange={(imageUrl) =>
                          updateQuestion(questionIndex, {
                            options: question.options.map((o, i) =>
                              i === optionIndex ? { ...o, imageUrl } : o,
                            ),
                          })
                        }
                      />
                      <FollowUpEditor
                        followUp={option.followUp}
                        optionLabel={option.optionId}
                        disabled={disabled}
                        onChange={(followUp) =>
                          updateQuestion(questionIndex, {
                            options: question.options.map((o, i) =>
                              i === optionIndex ? { ...o, followUp } : o,
                            ),
                          })
                        }
                      />
                    </li>
                  ))}
                </ol>

                <button
                  type="button"
                  className="ghost-add ghost-add-inset"
                  disabled={disabled || question.options.length >= MAX_OPTIONS_PER_QUESTION}
                  title={
                    question.options.length >= MAX_OPTIONS_PER_QUESTION
                      ? `A question takes at most ${MAX_OPTIONS_PER_QUESTION} options`
                      : undefined
                  }
                  onClick={() =>
                    updateQuestion(questionIndex, {
                      options: [...question.options, { optionId: newId('op'), text: '' }],
                    })
                  }
                >
                  Add option
                </button>
              </>
            )}
          </div>
        );
      })}

      <button
        type="button"
        className="ghost-add"
        disabled={disabled || screen.questions.length >= MAX_QUESTIONS_PER_SCREEN}
        title={
          screen.questions.length >= MAX_QUESTIONS_PER_SCREEN
            ? `A screen holds at most ${MAX_QUESTIONS_PER_SCREEN} questions`
            : undefined
        }
        onClick={() => onChange({ ...screen, questions: [...screen.questions, emptyQuestion()] })}
      >
        Add question
      </button>
    </section>
  );
}
